import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { applicantApi } from '../api/applicantApi';
import { jobApi } from '../api/jobApi';
import { jobBoardSettingsApi } from '../api/jobBoardSettingsApi';
import { lookupApi } from '../api/lookupApi';
import { publicJobBoardApi } from '../api/publicJobBoardApi';

import {
  ApplicationStatus,
  ApplyForJobRequest,
  CreateJobRequest,
  JobBoardSettingsDto,
  UpdateJobRequest,
} from '../types/jobBoard';

const OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  staleTime: 60 * 1000,
} as const;

export const useJobs = (
  enabled = true
) =>
  useQuery({
    queryKey: ['job-board', 'jobs'],
    queryFn: () =>
      jobApi.getJobs().then((res) => res.data),
    ...OPTIONS,
    enabled,
  });

export const useJob = (id: string | undefined) =>
  useQuery({
    queryKey: ['job-board', 'job', id],
    queryFn: () => jobApi.getJob(id as string).then((res) => res.data),
    enabled: Boolean(id),
    ...OPTIONS,
  });

export const useApplicants = (jobId: string | undefined) =>
  useQuery({
    queryKey: ['job-board', 'applicants', jobId],
    queryFn: () =>
      applicantApi
        .getApplicantsByJob(jobId as string)
        .then((res) => res.data),
    enabled: Boolean(jobId),
    ...OPTIONS,
  });

export const useApplicant = (
  jobId: string | undefined,
  applicantId: string | undefined
) => {
  const query = useApplicants(jobId);

  return {
    ...query,
    data: query.data?.find(
      (applicant) =>
        applicant.applicantId === applicantId ||
        applicant.id === applicantId
    ),
  };
};

export const useIndustries = () =>
  useQuery({
    queryKey: ['job-board', 'industries'],
    queryFn: () =>
      lookupApi.getIndustries().then((res) => res.data),
    ...OPTIONS,
  });

export const useCountries = () =>
  useQuery({
    queryKey: ['job-board', 'countries'],
    queryFn: () =>
      lookupApi.getCountries().then((res) => res.data),
    ...OPTIONS,
  });

export const useJobBoardSettings = () =>
  useQuery({
    queryKey: ['job-board', 'settings'],
    queryFn: () =>
      jobBoardSettingsApi
        .getSettings()
        .then((res) => res.data),
    ...OPTIONS,
  });

export const usePublicJobBoard = (
  slug: string | undefined
) =>
  useQuery({
    queryKey: ['public-job-board', slug],
    queryFn: () =>
      publicJobBoardApi
        .getSettings(slug as string)
        .then((res) => res.data),
    enabled: Boolean(slug),
    ...OPTIONS,
  });

export const usePublicJobs = (
  slug: string | undefined
) =>
  useQuery({
    queryKey: ['public-job-board', slug, 'jobs'],
    queryFn: () =>
      publicJobBoardApi
        .getJobs(slug as string)
        .then((res) => res.data),
    enabled: Boolean(slug),
    ...OPTIONS,
  });

export const usePublicJob = (
  slug: string | undefined,
  jobId: string | undefined
) =>
  useQuery({
    queryKey: ['public-job-board', slug, 'job', jobId],
    queryFn: () =>
      publicJobBoardApi
        .getJob(slug as string, jobId as string)
        .then((res) => res.data),
    enabled: Boolean(slug && jobId),
    ...OPTIONS,
  });

export const useCreateJob = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobRequest) =>
      jobApi.createJob(data).then((res) => res.data),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['job-board', 'jobs'],
      });

      toast.success('Job created successfully');
    },

    onError: () => {
      toast.error('Failed to create job');
    },
  });
};

export const useUpdateJob = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateJobRequest;
    }) =>
      jobApi.updateJob(id, data).then((res) => res.data),

    onSuccess: (_data, variables) => {
      client.invalidateQueries({
        queryKey: ['job-board', 'jobs'],
      });

      client.invalidateQueries({
        queryKey: ['job-board', 'job', variables.id],
      });

      toast.success('Job updated successfully');
    },

    onError: () => {
      toast.error('Failed to update job');
    },
  });
};

export const useUpdateApplicantStatus = (
  jobId: string | undefined
) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicantId,
      status,
    }: {
      applicantId: string;
      status: ApplicationStatus;
    }) =>
      applicantApi
        .updateStatus(applicantId, status)
        .then((res) => res.data),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['job-board', 'applicants', jobId],
      });

      toast.success('Applicant status updated');
    },

    onError: () => {
      toast.error('Failed to update applicant status');
    },
  });
};

export const useAllApplicants = () => {
  const jobs = useJobs();
  const jobIds = (jobs.data ?? []).map((job) => job.jobId);

  const results = useQueries({
    queries: jobIds.map((jobId) => ({
      queryKey: ['job-board', 'applicants', jobId],
      queryFn: () =>
        applicantApi
          .getApplicantsByJob(jobId)
          .then((res) => res.data),
      enabled: Boolean(jobId),
      ...OPTIONS,
    })),
  });

  const isLoading =
    jobs.isLoading || results.some((result) => result.isLoading);

  const data = results.flatMap((result, index) =>
    (result.data ?? []).map((applicant) => ({
      ...applicant,
      jobId: applicant.jobId || jobIds[index],
    }))
  );

  return { data, isLoading, jobs: jobs.data ?? [] };
};

export const useUpdateApplicantStatusAny = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicantId,
      status,
    }: {
      applicantId: string;
      jobId?: string;
      status: ApplicationStatus;
    }) =>
      applicantApi
        .updateStatus(applicantId, status)
        .then((res) => res.data),

    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['job-board'] });
    },

    onError: () => {
      toast.error('Failed to update applicant status');
    },
  });
};

export const useApplyForJob = () =>
  useMutation({
    mutationFn: (data: ApplyForJobRequest | FormData) =>
      applicantApi.apply(data).then((res) => res.data),

    onSuccess: () => {
      toast.success('Application submitted successfully');
    },

    onError: () => {
      toast.error(
        'Unable to submit application. Please try again.'
      );
    },
  });

export const useSaveJobBoardSettings = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (data: JobBoardSettingsDto) =>
      jobBoardSettingsApi
        .saveSettings(data)
        .then((res) => res.data),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['job-board', 'settings'],
      });

      toast.success('Job Board settings saved');
    },

    onError: () => {
      toast.error(
        'Failed to save Job Board settings'
      );
    },
  });
};